import React, { useState, useReducer, useCallback } from "react";
import {
    authenticateTokenPcap,
    authenticateLite,
    authenticatePart,
    onAuthenticateSuccess,
    onAuthenticateFailure
} from "../helpers/authenticate";
import { useNavigate } from "react-router-dom";
import LoginForm from "../LoginForm";
import useAuthenticateReducer, { initialState } from "../useAuthenticateReducer";
import redwoodAuthenticateToken from "../../../services/redwoodAuthenticateToken";
import loadFtd from "../helpers/loadFtd";
import localBuildConfig from "../../../../../../../build/buildConfig.json";
// We should be able to use the iframes window context to determine the login domain: ex: "proj10.retirementpartner.com"
const LOGIN_DOMAIN = window.location.origin || "https://participant.empower-retirement.com";

export interface LoginFormContainerNativeProps {
    accuCodeIn: string | null;
    fromIn: string;
    isLoginPartIn: boolean | null | undefined;
}
/**
 * Login Container for react only platform
 *    Also used by drupal iframe login page
 * @param param0
 * @returns
 */
export const multiAuth = localBuildConfig.multiAuth;
export const currentPcBaseApiUrl = multiAuth[window.subdomain];
const LoginFormContainerNative = ({
    accuCodeIn,
    fromIn,
    isLoginPartIn
}: LoginFormContainerNativeProps) => {
    const [accuCode, setAccuCode] = useState(accuCodeIn || "");
    const [loggingInState, setLoggingInState] = useState(false);
    const [showLogin, setShowLogin] = useState(true);
    const [authentication, dispatch] = useReducer(useAuthenticateReducer, initialState);
    const navigate = useNavigate();
    const getParamValue = () => {
        const paramName = "deeplink";
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const deeplinkParam = urlParams.get(paramName);
        return deeplinkParam || "";
    };

    const registerButtonClick = () => {
        setShowLogin(false);
    };

    const openAccountClick = () => {
        setShowLogin(false);
        const href = "/participant/#/articles/MYERIRA/openAnAccount";
        window.location.assign(href);
    };

    /**
     * on click submit
     */
    const handleAuthenticate = useCallback(
        async ({ username, password }) => {
            try {
                const isIframe = fromIn === "iframe";
                const deeplinkParam = getParamValue();
                const partAuthData =
                    !isIframe &&
                    (isLoginPartIn ||
                        accuCode === "Empower" ||
                        accuCode === "FREE" ||
                        accuCode === "MYERIRA")
                        ? await authenticatePart({
                              accuCode,
                              deeplinkParam,
                              username,
                              password,
                              setLoggingInState,
                              dispatch
                          })
                        : null;
                let authData;
                const idToken = partAuthData?.idToken;

                if (partAuthData?.authProvider === "EMPOWER") {
                    authData = await authenticateTokenPcap({
                        accuCode,
                        deeplinkParam,
                        idToken,
                        setLoggingInState,
                        dispatch
                    });
                } else if (partAuthData?.authProvider === "REDWOOD") {
                    if (idToken) {
                        const redwoodResponse = await redwoodAuthenticateToken(
                            currentPcBaseApiUrl,
                            idToken
                        );
                        loadFtd(redwoodResponse, navigate);
                    }
                } else {
                    authData = await authenticateLite({
                        accuCode,
                        deeplinkParam,
                        username,
                        password,
                        setLoggingInState,
                        dispatch
                    });
                }

                const href = await onAuthenticateSuccess({
                    accuCode,
                    authData,
                    dispatch,
                    setLoggingInState,
                    setAccuCode
                });

                let appendIframe = "";
                //Redirect to where it needs to go
                if (fromIn === "iframe") {
                    appendIframe = "iframe=true";
                }

                let redirectUrl =
                    deeplinkParam && deeplinkParam !== ""
                        ? href + "?noScope=true&deepLinkParam=" + deeplinkParam
                        : href;

                if (fromIn === "iframe") {
                    if (deeplinkParam && deeplinkParam !== "") {
                        redirectUrl = redirectUrl + "&" + appendIframe;
                    } else {
                        redirectUrl = redirectUrl + "?" + appendIframe;
                    }
                }

                //  Next line used for testing locally
                //  redirectUrl = "http://localhost:3005" + redirectUrl;

                window.location.assign(redirectUrl);
            } catch (error) {
                const href = onAuthenticateFailure({
                    error: error,
                    dispatch,
                    setLoggingInState,
                    setShowLogin,
                    loginDomain: LOGIN_DOMAIN,
                    accuCode
                });

                if (href) {
                    //Redirect to where it needs to go
                    window.location.assign(href);
                }
            }
        },
        [accuCode, fromIn, isLoginPartIn, navigate]
    );

    const updateStyleClass = {
        color: "white"
    };

    return (
        <div data-testid="login-container">
            <h1 style={updateStyleClass}>Test Landing Login page</h1>
            <LoginForm
                accu={accuCode}
                authentication={authentication}
                authenticateInput={handleAuthenticate}
                loggingIn={loggingInState}
                showLoginUpd={showLogin}
                errorCode={authentication.errorCode}
                errorMessageParams={authentication.errorMessageParams}
                registerButtonClickInput={registerButtonClick}
                openAccountClickInput={openAccountClick}
                loginDomain={LOGIN_DOMAIN}
                from={fromIn}
            />
        </div>
    );
};

export default LoginFormContainerNative;
